// Error messages for the app
export const ERROR = {
    SERVICE_ACCOUNT: {
        CREATE: "Failed to create service account",
        UPDATE: "Failed to update service account",
        REMOVE: "Failed to remove service account",
    },

    DEFAULT: "An error occurred. Please try again later.",

    LOGIN: {
        INCORRECT_CREDENTIALS: "Incorrect credentials",
        LOGIN_FAILED: "There is an issue with logging in. Please try again in a few minutes.",
        PASSWORD_CHANGE_REQUIRED: "Password change required. Please change your password before logging in.",
        NO_ORGANIZATIONS: "No organizations found. Please contact support.",
    },

    OTP: {
        OTP_NOT_SENT: "There is an issue with sending the OTP. Please try again in a few minutes.",
        MISSING_OTP: "Please enter the verification code",
        INVALID_OTP: "Invalid verification code. Please try again.",
        MISSING_PARAMS: "Missing required parameters. Please try logging in again.",
        RESEND_FAILED: "There is an issue with resending the OTP. Please try again in a few minutes.",
        EXPIRED_OTP: "Verification code has expired. Please request a new one.",
    },

    MFA: {
        STATUS_FAILED: "Failed to load authenticator settings. Please try again.",
        SETUP_FAILED: "Failed to start authenticator setup. Please try again.",
        CONFIRM_FAILED: "Invalid authenticator code. Please try again.",
        DISABLE_FAILED: "Failed to disable authenticator. Please try again.",
        ROTATE_FAILED: "Failed to generate new recovery codes. Please try again.",
        REAUTH_REQUIRED: "Enter your password and a current authenticator or recovery code.",
    },

    PASSWORD: {
        FORGOT_FAILED: "Failed to send password reset email. Please try again.",
        RESET_FAILED: "Failed to reset password. The token may be expired or invalid.",
        CHANGE_FAILED: "Failed to change password. Please try again.",
        INVALID_TOKEN: "Invalid or expired reset token",
        MISSING_TOKEN: "Missing reset token",
        WEAK_PASSWORD: "Password does not meet requirements",
        PASSWORDS_DONT_MATCH: "Passwords do not match",
        MISSING_EMAIL: "Please enter your email address",
        EASYLY_GUESSABLE_PASSWORD: "Password contains easily guessable patterns (e.g., month/year, common passwords like Pa55word, sequential patterns). Please choose a more secure password.",
    },

    GENERIC: {
        OPERATION_FAILED: "Operation failed. Please try again.",
        ACCOUNT_SWITCH_FAILED: "Failed to switch account. Please try again.",
    },

    ACCOUNT: {
        SENT_USAGE_EMAIL: "Failed to send usage email",
        NO_ACCOUNT_SELECTED: "No account selected. Please select an account first.",
        NO_SERVICE_LINE_OR_CUSTOMER_SELECTED: "No service line or customer selected. Please select a service line and customer first.",
    },

    ADDRESS: {
        ADD_TO_ACCOUNT: "Failed to add address to account",
        REMOVE_FROM_ACCOUNT: "Failed to remove address from account",
        SELECT_ACCOUNT: "Please select at least one account",
        CREATE: "Failed to create address",
        SEARCH: "Failed to search for address",
        SELECT_LOCATION_ON_MAP: "Please select a location on the map",
        ENTER_ADDRESS: "Please enter an address",
        UPDATE: "Failed to update address",
        SELECT_ADDRESS: "Please select an address",
        DELETE: "Failed to delete address",
    },

    SUBSCRIPTION: {
        CREATE: "Failed to create subscription",
        ADD_TO_ACCOUNT: "Failed to add subscription to account",
        REMOVE_FROM_ACCOUNT: "Failed to remove subscription from account",
        SELECT_ACCOUNT: "Please select at least one account",
        SELECT_STARLINK_SUBSCRIPTION: "Please select a Starlink subscription",
        DELETE: "Failed to delete subscription",
        UPDATE: "Failed to update subscription",
    },

    OVERAGE: {
        END_DATE_IN_PAST: "End date must be in the future",
        END_DATE_IN_FUTURE: "End date must be in the current month",
        AMOUNT_MUST_BE_GREATER_THAN_0: "Overage amount must be greater than 0",
    },

    TERMINAL: {
        DEVICE_IDS_REQUIRED: "Please enter at least one Device ID",
        TRANSFER_TO_ACCOUNT: "Failed to transfer terminal to account",
        CREATE: "Failed to create terminal",
        ADD_TO_STARLINK: "Failed to add terminal to Starlink",
        REMOVE_FROM_STARLINK: "Failed to remove terminal from Starlink",
        ADD_TO_ACCOUNT: "Failed to add terminal to account",
        REMOVE_FROM_ACCOUNT: "Failed to remove terminal from account",
        REBOOT: "Failed to reboot terminal",
        SELECT_ACCOUNT: "Please select at least one account",
        SELECT_TERMINALS: "Please select at least one terminal to transfer",
        UPDATE_TYPE: "Failed to update terminal type",
        NOTES: "Failed to add terminal notes",
        UPLOAD_IMAGES: "Failed to upload images",
        DELETE_IMAGE: "Failed to delete image",
    },

    SERVICE_LINE: {
        CREATE: "Failed to create service line",
        EDIT_NAME: "Failed to rename service line",
        DEACTIVATE: "Failed to deactivate service line",
        PAUSE: "Failed to pause service line",
        RESUME: "Failed to resume service line",
        SELECT_TERMINAL: "Please select a terminal",
        ADD_TERMINAL: "Failed to add terminal to service line",
        REMOVE_TERMINAL: "Failed to remove terminal from service line",
        SELECT_CUSTOMER: "Please select a customer",
        ADD_CUSTOMER: "Failed to add customer to service line",
        REMOVE_CUSTOMER: "Failed to remove customer from service line",
        UPDATE_SUBSCRIPTION_PLAN: "Failed to update subscription plan",
        ENABLE_OVERAGE: "Failed to enable overage",
        DISABLE_OVERAGE: "Failed to disable overage",
        UPDATE_PUBLIC_IP: "Failed to update public IP setting",
        UPDATE_TYPE: "Failed to update service line type",
        SELECT_TYPE: "Please select a service line type",
        ENTER_SERVICE_LINE_NAME: "Please enter a service line name",
        NOTES: "Failed to update service line notes",
        STARLINK_MINI_L2VPN_UNSUPPORTED: "Starlink does not support L2VPN over Starlink Mini terminal. If you want to deploy a Starlink Mini allocate a 'Priority data subscription'; if you want to deploy a L2VPN line, change the deployed terminal type.",
    },

    USER: {
        CREATE: "Failed to create user",
        UPDATE: "Failed to update user",
        REJECT: "Failed to reject user",
        REMOVE: "Failed to remove user",
        DELETE: "Failed to delete user",
    },

    CUSTOMER: {
        CREATE: "Failed to create customer",
        UPDATE: "Failed to update customer",
        SELECT_ACCOUNT: "Please select at least one account",
        REMOVE_FROM_SERVICE_LINE: "Failed to remove customer from service line",
        DELETE: "Failed to delete customer",
    },

    ROUTER: {
        REBOOT_REQUEST: "Failed to request router reboot",
        CREATE_CONFIG: "Failed to create Wi-Fi config",
        UPDATE_CONFIG: "Failed to update Wi-Fi config",
        DELETE_CONFIG: "Failed to delete Wi-Fi config",
        APPLY_CONFIG: "Failed to apply Wi-Fi config to router",
        ENTER_NAME: "Please enter a config name",
        ENTER_SSID: "Please enter a Wi-Fi name (SSID)",
        ENTER_PASSWORD: "Wi-Fi password must be 8–63 characters",
        SELECT_CONFIG: "Please select a Wi-Fi config",
    },

    L2VPN: {
        NO_MAPPINGS: "Please add at least one mapping",
        NO_ACCOUNT_SELECTED: "No account selected. Please select an account first.",
        ASSIGN_TO_TERMINAL: "Failed to assign L2VPN to terminal",
        REMOVE_FROM_TERMINAL: "Failed to remove L2VPN from terminal",
        ADD_TO_ACCOUNT: "Failed to add L2VPN to account",
        REMOVE_FROM_ACCOUNT: "Failed to remove L2VPN from account",
    },

    NOTIFICATIONS: {
        LOAD: "Failed to load notification preferences. Please try again.",
        UPDATE: "Failed to update notification preferences. Please try again.",
    },

    GLOBAL_DASHBOARD: {
        LOAD_ERROR: "Error loading global account overview.",
    },

    INVOICE: {
        CREATE_IN_ZOHO_FAILED: "Failed to create invoice in Zoho. Please try again.",
        MISSING_REQUIRED_FIELDS: "Missing required fields. Please select an organization and billing period.",
    },
};

// Success messages for the app
export const SUCCESS = {
    ACCOUNT: {
        SENT_USAGE_EMAIL: "Usage email sent successfully",
    },

    SERVICE_ACCOUNT: {
        CREATED: "Service account created successfully",
        UPDATED: "Service account updated successfully",
        REMOVED: "Service account removed successfully",
    },

    L2VPN: {
        ASSIGNED_TO_TERMINAL: "L2VPN assigned to terminal successfully",
        REMOVED_FROM_TERMINAL: "L2VPN removed from terminal successfully",
        ADDED_TO_ACCOUNT: "L2VPN added to account successfully",
        REMOVED_FROM_ACCOUNT: "L2VPN removed from account successfully",
    },

    LOGIN: {
        LOGIN_SUCCESS: "Login successful! Redirecting to home...",
    },

    ADDRESS: {
        ADDED_TO_ACCOUNT: "Address added to account successfully",
        REMOVED_FROM_ACCOUNT: "Address removed from account successfully",
        CREATED: "Address created successfully",
        UPDATED: "Address updated successfully",
        DELETED: "Address deleted successfully",
    },

    SUBSCRIPTION: {
        CREATED: "Subscription created successfully",
        ADDED_TO_ACCOUNT: "Subscription added to account successfully",
        REMOVED_FROM_ACCOUNT: "Subscription removed from account successfully",
        DELETED: "Subscription deleted successfully",
        UPDATED: "Subscription updated successfully",
    },

    TERMINAL: {
        TRANSFERRED_TO_ACCOUNT: "Terminal(s) transferred to account successfully",
        CREATED: "Terminal created successfully",
        ADDED_TO_STARLINK: "Terminal added to Starlink successfully",
        REMOVED_FROM_STARLINK: "Terminal removed from Starlink successfully",
        ADDED_TO_ACCOUNT: "Terminal added to account successfully",
        REMOVED_FROM_ACCOUNT: "Terminal removed from account successfully",
        REBOOTED: "Terminal rebooted successfully",
        UPDATED_TYPE: "Terminal type updated successfully",
        NOTES: "Terminal notes updated successfully",
        UPLOAD_IMAGES: "Images uploaded successfully",
        DELETE_IMAGE: "Image deleted successfully",
    },

    SERVICE_LINE: {
        CREATED: "Service line created successfully",
        EDITED_NAME: "Service line renamed successfully",
        DEACTIVATED: "Service line deactivated successfully",
        ADDED_TERMINAL: "Terminal added to service line successfully",
        REMOVED_TERMINAL: "Terminal removed from service line successfully",
        ADDED_CUSTOMER: "Customer added to service line successfully",
        REMOVED_CUSTOMER: "Customer removed from service line successfully",
        UPDATED_SUBSCRIPTION_PLAN: "Subscription plan updated successfully",
        ENABLED_OVERAGE: "Overage updated successfully",
        DISABLED_OVERAGE: "Overage updated successfully",
        UPDATED_PUBLIC_IP: "Public IP setting updated successfully",
        UPDATED_TYPE: "Service line type updated successfully",
        RESUMED: "Service line resumed successfully",
        PAUSED: "Service line paused successfully",
        NOTES: "Service line notes updated successfully",
    },

    USER: {
        CREATED: "User created successfully",
        UPDATED: "User updated successfully",
        REJECTED: "User rejected successfully",
        APPROVED: "User approved successfully",
        REMOVED: "User removed successfully",
        DELETED: "User deleted successfully",
    },

    PASSWORD: {
        RESET_EMAIL_SENT: "Password reset email sent! Please check your inbox.",
        RESET_SUCCESS: "Password reset successfully!",
        CHANGE_SUCCESS: "Password changed successfully!",
    },

    OTP: {
        VERIFIED: "Verification successful! Logging you in...",
        RESENT: "Verification code sent! Please check your email.",
        EMAIL_BACKUP_SENT: "We sent a verification code to your email.",
    },

    MFA: {
        ENABLED: "Authenticator app enabled.",
        DISABLED: "Authenticator app disabled. Email verification will be used at login.",
        RECOVERY_ROTATED: "New recovery codes generated.",
    },

    NOTIFICATIONS: {
        UPDATED: "Notification preferences saved.",
    },

    ROUTER: {
        REBOOT_REQUESTED: "Router reboot requested successfully",
        CONFIG_CREATED: "Wi-Fi config created successfully",
        CONFIG_UPDATED: "Wi-Fi config updated successfully",
        CONFIG_DELETED: "Wi-Fi config deleted successfully",
        CONFIG_APPLIED: "Wi-Fi config applied to router successfully",
    },

    CUSTOMER: {
        CREATED: "Customer created successfully",
        UPDATED: "Customer updated successfully",
        REMOVED_FROM_SERVICE_LINE: "Customer removed from service line successfully",
        DELETED: "Customer deleted successfully",
    },

    INVOICE: {
        CREATED_IN_ZOHO: "Invoice created in Zoho successfully: {{invoiceNumber}} with invoice ID: {{invoiceId}}",
    },
};